/** @jsxImportSource @emotion/react */

import { useEffect, useRef } from 'react';

import useStatefulInput, {
  IInputValidator,
} from '../../../../../hooks/useStatefulInput';
import useAction, { IAction } from '../../../../../hooks/useAction';
import Toggle from '../../../../../components/toggle';
import useTheme from '../../../../../hooks/useTheme';
import { IBlockBaseItem } from '../blockItem';

export interface IBlockToggleItem extends IBlockBaseItem {
  type: 'toggle';

  inputId: string;
  active: boolean;
  enabled: boolean;
  inactiveColor: string;
  activeColor: string;
  validator?: IInputValidator;

  action: IAction;
}

interface IBlockToggleItemProps {
  data: IBlockToggleItem;
}

const BlockToggleItem = ({ data, ...props }: IBlockToggleItemProps) => {
  const {
    inputValue,
    isValid,
    isDirty,
    isTouched,
    setInputValue,
    setIsTouched,
  } = useStatefulInput<boolean>({
    inputId: data.inputId,
    initialValue: data.active,
    validators: data.validator ? [data.validator] : undefined,
  });

  const { resolveColor } = useTheme();
  const handleAction = useAction();
  const shouldShowErrorState = !isValid && isDirty && isTouched;

  const prevValueRef = useRef<boolean>(!!inputValue);

  const actionRef = useRef(data.action);

  // Keep ref up to date
  useEffect(() => {
    actionRef.current = data.action;
  }, [data.action]);

  // handleAction relies on ScreenContext,    this ensures the context state is updated
  // before submitting the request to avoid race conditions.
  useEffect(() => {
    const currentValue = !!inputValue;
    const prevValue = prevValueRef.current;

    if (currentValue !== prevValue) {
      prevValueRef.current = currentValue;

      // Only runs if inputValue and data.action changes
      handleAction(actionRef.current);
    }
  }, [handleAction, inputValue]);

  return (
    <div {...props}>
      <Toggle
        checked={!!inputValue}
        enabled={data.enabled}
        activeColor={resolveColor(data.activeColor)}
        inactiveColor={resolveColor(data.inactiveColor)}
        onChange={(e) => {
          e.stopPropagation();
          setInputValue(!!e.target.checked);
        }}
        onBlur={() => {
          setIsTouched();
        }}
        invalid={shouldShowErrorState}
        aria-invalid={shouldShowErrorState}
        // aria-errormessage={`${data.inputId}-validation-error`}
      />
    </div>
  );
};

export default BlockToggleItem;
